import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Animated,
  Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { documentSharingAPI } from '@/services/api';
import { colors } from '@/design-system/theme/colors';
import { useTranslation } from '@/hooks/useTranslation';
import { useResponsive } from '@/hooks/useResponsive';
import type { DocumentRecipient, ShareRecipient } from '@/types/sharing';
import { LibraryErrorState } from '@/components/library/LibraryErrorState';
import { actionErrorMessage } from '@/screens/chat/chatErrors';
import { brand } from '../../design-system/theme/tokens';

const SHEET_HEIGHT_RATIO = 0.7;
const BACKDROP_OPACITY = 0.4;
const ANIMATION_DURATION = 320;

interface ManageShareRecipientsModalProps {
  visible: boolean;
  onClose: () => void;
  documentId: number;
  documentName: string;
  onRecipientsChanged?: () => void;
}

type ViewMode = 'recipients' | 'add';

const ROLE_CONFIG: Record<string, { label: string; bg: string; fg: string }> = {
  b2b2c_student: { label: 'Student', bg: '#10B98115', fg: '#10B981' },
  b2b2c_mentor: { label: 'Mentor', bg: `${brand.primary}15`, fg: brand.primary },
  b2b2c_admin: { label: 'Admin', bg: '#F59E0B15', fg: '#F59E0B' },
};

function getRoleDisplay(userType?: string) {
  if (!userType) return { label: 'Member', bg: colors.gray[100], fg: colors.gray[500] };
  return ROLE_CONFIG[userType] ?? { label: 'Member', bg: colors.gray[100], fg: colors.gray[500] };
}

function getBroadcastLabel(type: string, t: (k: string, f?: string) => string): string {
  if (type === 'all_students') return t('share.allStudents');
  if (type === 'all_members') return t('share.allMembers');
  return type;
}

export function ManageShareRecipientsModal({
  visible,
  onClose,
  documentId,
  documentName,
  onRecipientsChanged,
}: ManageShareRecipientsModalProps) {
  const { t } = useTranslation();
  // Track the live window height (rotation / browser resize) instead of a
  // module-load snapshot (CHAT-27).
  const { height: windowHeight } = useResponsive();
  const sheetHeight = windowHeight * SHEET_HEIGHT_RATIO;
  const sheetHeightRef = useRef(sheetHeight);
  sheetHeightRef.current = sheetHeight;

  const slideAnim = useRef(new Animated.Value(sheetHeight)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  const [viewMode, setViewMode] = useState<ViewMode>('recipients');
  const [recipients, setRecipients] = useState<DocumentRecipient[]>([]);
  const [availableRecipients, setAvailableRecipients] = useState<ShareRecipient[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  // CHAT-10: a failed load shows an error with retry, not "No recipients yet"
  // (which invites re-sharing) or "Shared with everyone".
  const [loadFailed, setLoadFailed] = useState(false);
  const [addLoadFailed, setAddLoadFailed] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // ─── Animation ───────────────────────────────────────────────────────

  const animateIn = useCallback(() => {
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: 0,
        damping: 28,
        stiffness: 300,
        mass: 0.8,
        useNativeDriver: true,
      }),
      Animated.timing(backdropAnim, {
        toValue: BACKDROP_OPACITY,
        duration: ANIMATION_DURATION,
        useNativeDriver: true,
      }),
    ]).start();
  }, [slideAnim, backdropAnim]);

  const animateOut = useCallback(() => {
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: sheetHeightRef.current,
        damping: 28,
        stiffness: 300,
        mass: 0.8,
        useNativeDriver: true,
      }),
      Animated.timing(backdropAnim, {
        toValue: 0,
        duration: ANIMATION_DURATION * 0.6,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onClose();
    });
  }, [slideAnim, backdropAnim, onClose]);

  // ─── Data loading ────────────────────────────────────────────────────

  const loadRecipients = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await documentSharingAPI.getDocumentRecipients(documentId);
      setRecipients(data);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const loadAvailableRecipients = useCallback(async () => {
    setAddLoading(true);
    setAddLoadFailed(false);
    try {
      const all = await documentSharingAPI.getRecipients();
      // Filter out already-shared recipients
      const existingIds = new Set(
        recipients
          .filter((r) => r.recipient.type === 'individual' && r.recipient.id)
          .map((r) => r.recipient.id!)
      );
      setAvailableRecipients(all.filter((r) => !existingIds.has(r.id)));
    } catch {
      setAvailableRecipients([]);
      setAddLoadFailed(true);
    } finally {
      setAddLoading(false);
    }
  }, [recipients]);

  // ─── Lifecycle ───────────────────────────────────────────────────────

  useEffect(() => {
    if (visible) {
      slideAnim.setValue(sheetHeightRef.current);
      backdropAnim.setValue(0);
      setViewMode('recipients');
      setSelectedIds(new Set());
      loadRecipients();
      requestAnimationFrame(() => animateIn());
    }
  }, [visible, loadRecipients, animateIn, slideAnim, backdropAnim]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    animateOut();
  }, [animateOut]);

  const handleRemove = useCallback(
    async (shareId: number) => {
      const doRemove = async () => {
        setRemovingId(shareId);
        try {
          await documentSharingAPI.revokeShare(shareId);
          setRecipients((prev) => prev.filter((r) => r.share_id !== shareId));
          onRecipientsChanged?.();
        } catch (error) {
          showDialog(actionErrorMessage(t, error, 'chatLibrary.errors.removeRecipientFailed'));
        } finally {
          setRemovingId(null);
        }
      };

      const confirmed = await confirmDialog(
        t('share.removeRecipientTitle'),
        t('share.removeRecipientConfirm'),
        t('common.remove'),
        t('common.cancel'),
      );
      if (confirmed) await doRemove();
    },
    [onRecipientsChanged, t]
  );

  const handleSwitchToAdd = useCallback(() => {
    setViewMode('add');
    setSelectedIds(new Set());
    loadAvailableRecipients();
  }, [loadAvailableRecipients]);

  const handleBackToRecipients = useCallback(() => {
    setViewMode('recipients');
    setSelectedIds(new Set());
  }, []);

  const toggleRecipient = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddRecipients = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      await documentSharingAPI.addDocumentRecipients(documentId, {
        recipient_user_ids: Array.from(selectedIds),
      });
      onRecipientsChanged?.();
      // Reload and switch back
      await loadRecipients();
      setViewMode('recipients');
      setSelectedIds(new Set());
    } catch (error) {
      showDialog(actionErrorMessage(t, error, 'chatLibrary.errors.addRecipientsFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [documentId, selectedIds, loadRecipients, onRecipientsChanged, t]);

  // ─── Render helpers ──────────────────────────────────────────────────

  const renderRecipientRow = ({ item }: { item: DocumentRecipient }) => {
    const isBroadcast = item.recipient.type !== 'individual';
    const role = getRoleDisplay(item.recipient.user_type);
    const isRemoving = removingId === item.share_id;

    return (
      <View style={[s.recipientRow, isRemoving && { opacity: 0.5 }]}>
        {isBroadcast ? (
          <View style={[s.avatar, { backgroundColor: `${colors.primary}15` }]}>
            <MaterialIcons name="groups" size={20} color={colors.primary} />
          </View>
        ) : (
          <View style={s.avatar}>
            <Text style={s.avatarText}>
              {(item.recipient.full_name ?? '?').charAt(0).toUpperCase()}
            </Text>
          </View>
        )}

        <View style={s.recipientInfo}>
          <Text style={s.recipientName} numberOfLines={1}>
            {isBroadcast
              ? getBroadcastLabel(item.recipient.type, t)
              : item.recipient.full_name ?? 'Unknown'}
          </Text>
          {!isBroadcast && (
            <View style={[s.roleBadge, { backgroundColor: role.bg }]}>
              <Text style={[s.roleBadgeText, { color: role.fg }]}>{role.label}</Text>
            </View>
          )}
        </View>

        <TouchableOpacity
          onPress={() => handleRemove(item.share_id)}
          disabled={isRemoving}
          style={s.removeBtn}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {isRemoving ? (
            <ActivityIndicator size="small" color={colors.error} />
          ) : (
            <MaterialIcons name="close" size={18} color={colors.error} />
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const renderAddRecipientRow = ({ item }: { item: ShareRecipient }) => {
    const isSelected = selectedIds.has(item.id);
    const role = getRoleDisplay(item.user_type);

    return (
      <TouchableOpacity
        style={[s.recipientRow, isSelected && s.recipientRowSelected]}
        onPress={() => toggleRecipient(item.id)}
        activeOpacity={0.7}
      >
        <View style={s.avatar}>
          <Text style={s.avatarText}>{item.full_name.charAt(0).toUpperCase()}</Text>
        </View>

        <View style={s.recipientInfo}>
          <Text style={s.recipientName} numberOfLines={1}>
            {item.full_name}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <View style={[s.roleBadge, { backgroundColor: role.bg }]}>
              <Text style={[s.roleBadgeText, { color: role.fg }]}>{role.label}</Text>
            </View>
            <Text style={s.recipientEmail} numberOfLines={1}>
              {item.email}
            </Text>
          </View>
        </View>

        <View style={[s.checkbox, isSelected && s.checkboxChecked]}>
          {isSelected && <MaterialIcons name="check" size={14} color="#FFF" />}
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmptyRecipients = () => (
    <View style={s.emptyContainer}>
      <MaterialIcons name="people-outline" size={48} color={colors.gray[300]} />
      <Text style={s.emptyTitle}>
        {t('share.noRecipientsYet')}
      </Text>
      <Text style={s.emptySubtitle}>
        {t('share.addRecipientsHint')}
      </Text>
    </View>
  );

  const renderEmptyAvailable = () => (
    <View style={s.emptyContainer}>
      <MaterialIcons name="check-circle-outline" size={48} color={colors.gray[300]} />
      <Text style={s.emptyTitle}>
        {t('share.allShared')}
      </Text>
      <Text style={s.emptySubtitle}>
        {t('share.allSchoolMembersHaveAccess')}
      </Text>
    </View>
  );

  // ─── Main render ─────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      transparent
      statusBarTranslucent
      animationType="none"
      onRequestClose={handleDismiss}
    >
      {/* Backdrop */}
      <TouchableWithoutFeedback onPress={handleDismiss}>
        <Animated.View style={[s.backdrop, { opacity: backdropAnim }]} />
      </TouchableWithoutFeedback>

      {/* Sheet */}
      <Animated.View style={[s.sheet, { height: sheetHeight, transform: [{ translateY: slideAnim }] }]}>
        {/* Drag Handle */}
        <View style={s.handleContainer}>
          <View style={s.handle} />
        </View>

        {/* Header */}
        <View style={s.header}>
          {viewMode === 'add' ? (
            <TouchableOpacity
              onPress={handleBackToRecipients}
              style={s.headerBackBtn}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MaterialIcons name="arrow-back" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          ) : (
            <View style={s.headerBackBtn} />
          )}

          <View style={s.headerCenter}>
            <Text style={s.headerTitle} numberOfLines={1}>
              {viewMode === 'recipients'
                ? t('share.manageRecipients')
                : t('share.addMore')}
            </Text>
            <Text style={s.headerSubtitle} numberOfLines={1}>
              {documentName}
            </Text>
          </View>

          <View style={s.headerBackBtn} />
        </View>

        {/* Content */}
        {viewMode === 'recipients' ? (
          <>
            {loading ? (
              <View style={s.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : loadFailed ? (
              <LibraryErrorState
                title={t('chatLibrary.library.recipientsLoadError')}
                body={t('chatLibrary.library.loadErrorBody')}
                retryLabel={t('common.retry')}
                onRetry={loadRecipients}
                testID="share_recipients_load_error"
              />
            ) : (
              <FlatList
                data={recipients}
                keyExtractor={(item) => String(item.share_id)}
                renderItem={renderRecipientRow}
                ListEmptyComponent={renderEmptyRecipients}
                contentContainerStyle={recipients.length === 0 ? s.emptyList : s.listContent}
                showsVerticalScrollIndicator={false}
                ItemSeparatorComponent={() => <View style={s.separator} />}
              />
            )}

            {/* Add More button */}
            <View style={s.footerContainer}>
              <TouchableOpacity
                style={s.addMoreBtn}
                onPress={handleSwitchToAdd}
                activeOpacity={0.8}
              >
                <MaterialIcons name="person-add" size={20} color={colors.primary} />
                <Text style={s.addMoreText}>
                  {t('share.addMore')}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        ) : (
          <>
            {addLoading ? (
              <View style={s.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : addLoadFailed ? (
              <LibraryErrorState
                title={t('chatLibrary.library.recipientsLoadError')}
                body={t('chatLibrary.library.loadErrorBody')}
                retryLabel={t('common.retry')}
                onRetry={loadAvailableRecipients}
                testID="share_available_load_error"
              />
            ) : (
              <FlatList
                data={availableRecipients}
                keyExtractor={(item) => String(item.id)}
                renderItem={renderAddRecipientRow}
                ListEmptyComponent={renderEmptyAvailable}
                contentContainerStyle={
                  availableRecipients.length === 0 ? s.emptyList : s.listContent
                }
                showsVerticalScrollIndicator={false}
                ItemSeparatorComponent={() => <View style={s.separator} />}
              />
            )}

            {/* Confirm button */}
            {availableRecipients.length > 0 && (
              <View style={s.footerContainer}>
                <TouchableOpacity
                  style={[
                    s.confirmBtn,
                    selectedIds.size === 0 && { opacity: 0.5 },
                  ]}
                  onPress={handleAddRecipients}
                  disabled={selectedIds.size === 0 || submitting}
                  activeOpacity={0.8}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#FFF" />
                  ) : (
                    <>
                      <MaterialIcons name="check" size={20} color="#FFF" />
                      <Text style={s.confirmBtnText}>
                        {selectedIds.size > 0
                          ? t('share.addCount', { count: selectedIds.size })
                          : t('share.selectRecipients')}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </>
        )}
      </Animated.View>
    </Modal>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
  },
  sheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.background,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      },
      android: {
        elevation: 16,
      },
    }),
  },
  handleContainer: {
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 4,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.gray[300],
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerBackBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.text.primary,
    letterSpacing: -0.3,
  },
  headerSubtitle: {
    fontSize: 13,
    color: colors.text.muted,
    marginTop: 2,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // List
  listContent: {
    paddingVertical: 8,
  },
  separator: {
    height: 1,
    backgroundColor: colors.border,
    marginLeft: 68,
  },

  // Recipient row
  recipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  recipientRowSelected: {
    backgroundColor: colors.interactive.hover,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFF',
  },
  recipientInfo: {
    flex: 1,
    gap: 3,
  },
  recipientName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text.primary,
  },
  recipientEmail: {
    fontSize: 12,
    color: colors.text.muted,
    flex: 1,
  },
  roleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  roleBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  removeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#EF444410',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },

  // Checkbox (add mode)
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.gray[300],
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  // Footer
  footerContainer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: Platform.OS === 'ios' ? 28 : 16,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background,
  },
  addMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderStyle: 'dashed',
  },
  addMoreText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  confirmBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.primary,
  },
  confirmBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFF',
  },

  // Empty
  emptyList: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: 14,
    marginBottom: 6,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.text.muted,
    textAlign: 'center',
  },
});

export default ManageShareRecipientsModal;
